import { useState, useMemo } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  useChoices,
  useGetAllSOPs,
  usePostWorkOrderJobCard,
  usePriority,
} from "@features/mx/device-management/hooks";
import { mapToLabelValue } from "@shared/utils/mapToLabelValue";

interface CreateServiceOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  resource: any;
}

export function CreateServiceOrderModal({
  isOpen,
  onClose,
  resource,
}: CreateServiceOrderModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const remoteUtilityId = useRemoteUtilityId();
  const [serviceType, setServiceType] = useState("Resource Replacement");
  
  const { mutate: createJobCard } = usePostWorkOrderJobCard();
  const { data: priorityData } = usePriority();
  const { data: serviceTypesData } = useChoices({
    choice: "service_order_operation_type",
  });

  const serviceTypeOptions =
    serviceTypesData?.map((serviceTypes) => ({
      value: serviceTypes.key,
      label: serviceTypes.value,
    })) || [];
  const priorityOptions =
    priorityData?.result?.map((priority) => ({
      value: priority.key,
      label: priority.value,
    })) || [];

  const { data: sops } = useGetAllSOPs({
    remoteUtilityId,
    operationType: serviceType,
  });

  const sopList = mapToLabelValue(sops?.result, "name", "id");
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "serviceType",
        label: "Service Type",
        type: "select",
        required: true,
        placeholder: "Select work type",
        options: serviceTypeOptions,
      },
      {
        name: "sop",
        type: "select",
        label: "SOP Template",
        options: sopList,
        required: true,
        placeholder: "Select service order",
      },
      {
        name: "priority",
        label: "Priority",
        type: "select",
        required: true,
        placeholder: "Select priority",
        options: priorityOptions,
      },
      {
        name: "date",
        label: "Expected Date",
        type: "datepicker",
        required: true,
        allowPast: false,
        placeholder: "Select date",
      },
      {
        name: "description",
        label: "Description",
        type: "textarea",
        placeholder: "Enter description",
        className: "col-span-2 mt-4",
      },
    ],
    [serviceTypeOptions, priorityOptions, sopList],
  );

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    defaultValues: {
      workType: "",
      priority: "",
      date: "",
      description: "",
    },
  });

  const handleSubmit = (data: any) => {
    setIsSubmitting(true);

    const payload = {
      additional_data: {
        assignment_status: resource?.assignmentStatus || resource?.status,
        device_no: resource?.deviceNo,
        physical_status: resource?.physicalStatus,
        reading_status: resource?.assignmentStatus || resource?.status,
      },
      latitude: resource?.lat || resource?.latitude,
      area: resource?.areaCode || resource?.area,
      sub_area: resource?.subAreaCode || resource?.subArea,
      longitude: resource?.long || resource?.longitude,
      premise: resource?.premise,
      remark: data.description,
      remote_utility_id: remoteUtilityId,
      request_account_no: resource?.deviceNo || resource?.id,
      request_id: resource?.id,
      priority: data.priority,
      sop: data.sop,
      schedule_date: data.date,
    };

    createJobCard(payload, {
      onSuccess: () => {
        setIsSubmitting(false);
        onClose();
      },
      onError: (err: any) => {
        setIsSubmitting(false);
      },
    });
  };

  const handleFieldChange = (fieldName, value) => {
    if (fieldName === "serviceType") {
      setServiceType(value);
    }
  };

  const handleCancel = () => {
    form.reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Service Order</DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto max-h-[calc(90vh-200px)] px-1">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            onFieldChange={handleFieldChange}
            gridColumns={2}
            config={{
              showSubmitButton: false,
              showResetButton: false,
              layout: "vertical",
              spacing: "comfortable",
              classes: FIELD_CLASSES,
            }}
            renderCustomFooter={() => (
              <div className="flex items-center justify-end gap-3 pt-6 border-t mt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={form.handleSubmit(handleSubmit)}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Creating..." : "Create"}
                </Button>
              </div>
            )}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
